import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import {
  PrimaryButton,
  SecondaryButton
} from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { ShoppingCartItem } from '@dtx-company/flow-codegen/src/code/generated.types'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { convertToPrice } from '../../../utils/print-store'
import { useCallback, useEffect, useRef } from 'react'
import { useCartItemsPrice } from '../hooks/useCartItemsPrice'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils'
import { useRouter } from 'next/router'
import { useShoppingCartQuery } from '@dtx-company/inter-app/src/redux/slices/flowcodeApiSlice/printStoreQueries'
import { useShouldShowV3DesktopNavigation } from '@app/common/src/hooks/navv3/useShouldShowV3DesktopNavigation/useShouldShowV3DesktopNavigation'
import printStoreEvents from '@dtx-company/inter-app/src/event-tracking/events/printStore'
import shoppingCartEvents from '@dtx-company/inter-app/src/event-tracking/events/ShoppingCart'
import styled from 'styled-components'

const UpArrow = styled(Box)`
  height: 12px;
  width: 12px;
  transform: rotate(45deg);
  border-top: 1px solid #d9d9d9;
  border-left: 1px solid #d9d9d9;
  position: absolute;
  top: -7px;
  right: 52px;
`

export const ShoppingCartAddedItemModal = (): JSX.Element | null => {
  const { data } = useShoppingCartQuery({})
  const cartItems = data?.shoppingCart?.items || []
  const lastAddedItem = cartItems[cartItems.length - 1]
  const { modalOpen, setClose } = useModal(ModalTypes.SHOPPING_CART_RECENTLY_ADDED_ITEM_MODAL)
  const { getItemPrice } = useCartItemsPrice()
  const router = useRouter()
  const modalRef = useRef<HTMLDivElement>(null)

  const closeHandler = useCallback(
    (e: MouseEvent | TouchEvent) => {
      const modal = modalRef.current
      if (modal) {
        const isOutside = !modal.contains(e.target as Element)
        if (isOutside) {
          setClose && setClose()
        }
      }
    },
    [modalRef, setClose]
  )

  useEffect(() => {
    if (modalOpen) {
      document.addEventListener('mousedown', closeHandler)
    } else {
      document.removeEventListener('mousedown', closeHandler)
    }
    return () => {
      document.removeEventListener('mousedown', closeHandler)
    }
  }, [modalOpen, closeHandler])

  const addedItemPrice = lastAddedItem
    ? convertToPrice(getItemPrice(lastAddedItem as ShoppingCartItem))
    : null

  const { shouldMountTopNav } = useShouldShowV3DesktopNavigation()

  return modalOpen ? (
    <Box
      padding="20px"
      position="absolute"
      backgroundColor="primary.white"
      border="1px solid #D9D9D9"
      top={shouldMountTopNav ? '10px' : '44px'}
      right={shouldMountTopNav ? '230px' : '9px'}
      minWidth="370px"
      flexDirection="column"
      data-testid="shopping-cart-recently-added"
      ref={modalRef}
    >
      <UpArrow backgroundColor={'primary.white'} />
      <Box justifyContent={'space-between'} width="100%">
        <Box alignItems="center">
          <Icon src={'/icons/checkmark-filled.svg'} height="20px" width="20px" mr="12px" />
          <Text variant="body/medium">Added to cart</Text>
        </Box>
        <Icon
          src={'/icons/close-filled-black.svg'}
          height="20px"
          width="20px"
          onClick={setClose}
          cursor="pointer"
        />
      </Box>
      {lastAddedItem && (
        <Box mt="28px">
          {lastAddedItem?.product?.imageUrls?.length && (
            <Image
              src={lastAddedItem?.product?.imageUrls?.[0] || ''}
              alt={`image of ${lastAddedItem?.product?.name}`}
              mr="12px"
              maxWidth="100px"
              data-testid="shopping-cart-recently-added-item-image"
            />
          )}
          <Box flexDirection="column" justifyContent="space-between">
            <Box flexDirection="column">
              {lastAddedItem?.product?.name && (
                <Text
                  fontSize="12px"
                  fontWeight={600}
                  data-testid="shopping-cart-recently-added-item-name"
                >
                  {lastAddedItem?.product?.name || ''}
                </Text>
              )}
              {lastAddedItem?.quantity && (
                <Text
                  fontSize="12px"
                  data-testid="shopping-cart-recently-added-item-quantity"
                >{`Quantity: ${lastAddedItem?.quantity}`}</Text>
              )}
            </Box>
            {addedItemPrice && (
              <Text
                fontSize="12px"
                fontWeight={600}
                data-testid="shopping-cart-recently-added-item-price"
              >{`$${addedItemPrice}`}</Text>
            )}
          </Box>
        </Box>
      )}
      <Box mt="32px">
        <SecondaryButton
          backgroundColor="secondary.backgroundDark"
          borderColor="secondary.border"
          textVariant="button/general"
          onClick={() => {
            router.push(Routes.PRINT_STORE_LANDING_PAGE)
            shoppingCartEvents.Add_To_Cart_Modal_Clicked_Keep_Shopping_Btn()
            setClose()
          }}
          data-testid="shopping-cart-recently-added-keep-shopping-btn"
        >
          Keep shopping
        </SecondaryButton>
        <Spacer mr="8px" />
        <PrimaryButton
          textVariant="button/general"
          onClick={() => {
            printStoreEvents.Print_Store_PDP_Clicked_View_Cart_Button('ShoppingCartAddedItemModal')
            router.push(Routes.SHOPPING_CART)
            setClose()
          }}
          data-testid="shopping-cart-recently-added-view-cart-btn"
        >
          {cartItems ? `View cart (${cartItems?.length})` : 'View cart'}
        </PrimaryButton>
      </Box>
    </Box>
  ) : null
}
